/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import createContact, {
  CreateContactFailure,
  CreateContactPayload,
} from '../../../../domain/use_cases/contacts/createContact';
import AuthenticationContext from '../../auth/AuthenticationContext';
import Subscription from '../../../../domain/models/Subscription';
import getSubscriptionById from '../../../../domain/use_cases/subscriptions/getSubscriptionById';
import { EContactCivility } from '@leedpulse-monorepo/enums';
import UserDoc from '../../../../domain/models/UserDoc';
import getUsersBySubscriptionId from '../../../../domain/use_cases/users/getUsersBySubscriptionId';
import getScenariosByIds from '../../../../domain/use_cases/scenarios/getScenariosByIds';
import Scenario from '../../../../domain/models/Scenario';
import { formatErrorMessage } from '../../../../core/utils/errorUtils';
import { message } from 'antd';

const ContactCreationFormContext = createContext({
  status: ContextStatus.IDLE,
  subscription: null as Subscription | null,
  subscriptionOwners: [] as UserDoc[],
  subscriptionScenarios: [] as Scenario[],
  civility: EContactCivility.empty,
  firstname: '',
  lastname: '',
  phone: '',
  email: '',
  owner: '',
  details: '',
  sender: '',
  prospectCategory: '',
  company: '',
  adresse: '',
  role: '',
  website: '',
  contactScheduled: true,
  contactScheduledDate: dayjs() as Dayjs | null,
  errorMessage: '',
  initWithSubscriptionId: (_: string, __: Scenario[] = []) => {},
  onCivilityChange: (_: EContactCivility) => {},
  onFirstnameChange: (_: string) => {},
  onLastnameChange: (_: string) => {},
  onPhoneChange: (_: string) => {},
  onEmailChange: (_: string) => {},
  onOwnerChange: (_: string) => {},
  onDetailsChange: (_: string) => {},
  onSenderChange: (_: string) => {},
  onProspectCategoryChange: (_: string) => {},
  onCompanyChange: (_: string) => {},
  onAdresseChange: (_: string) => {},
  onRoleChange: (_: string) => {},
  onWebsiteChange: (_: string) => {},
  onContactScheduledChange: (_: boolean) => {},
  onContactScheduledDateChange: (_: Dayjs | null) => {},
  submit: () => {},
});

export const ContactCreationFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [subscription, setSubscription] = useState(null as Subscription | null);
  const [subscriptionOwners, setSubscriptionOwners] = useState([] as UserDoc[]);
  const [subscriptionScenarios, setSubscriptionScenarios] = useState([] as Scenario[]);
  const [civility, setCivility] = useState<EContactCivility>(EContactCivility.empty);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [owner, setOwner] = useState('');
  const [details, setDetails] = useState('');
  const [sender, setSender] = useState('');
  const [prospectCategory, setProspectCategory] = useState('');
  const [company, setCompany] = useState('');
  const [adresse, setAdresse] = useState('');
  const [role, setRole] = useState('');
  const [website, setWebsite] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [contactScheduled, setContactScheduled] = useState(true);
  const [contactScheduledDate, setContactScheduledDate] = useState<Dayjs | null>(dayjs());

  const authContext = useContext(AuthenticationContext);

  const initWithSubscriptionId = async (subscriptionId: string, globalScenarios: Scenario[] = []) => {
    try {
      const subscription = await getSubscriptionById(subscriptionId);
      if (subscription === null) throw new Error('Subscription not found');
      const owners = await getUsersBySubscriptionId(subscriptionId);
      const scenarios = await getScenariosByIds(subscription.subscription.idScenarios || []);
      setSubscription(subscription?.subscription);
      setSubscriptionOwners(owners);
      setSubscriptionScenarios([...globalScenarios, ...scenarios]);
      if (owners.length < 2) {
        setOwner(owners[0].uid || '');
      }
      if (scenarios.length < 2) {
        setProspectCategory(scenarios[0]?.id || '');
      }
      if (subscription?.subscription?.sender.length < 2) {
        setSender(subscription?.subscription?.sender[0] || '');
      }
    } catch (e) {
      setStatus(ContextStatus.ERROR);
      message.error(formatErrorMessage((e as Error).message));
    }
  };

  const onCivilityChange = (civility: EContactCivility) => {
    setCivility(civility);
  };

  const onFirstnameChange = (firstname: string) => {
    setFirstname(firstname);
  };

  const onLastnameChange = (lastname: string) => {
    setLastname(lastname);
  };

  const onPhoneChange = (phone: string) => {
    setPhone(phone);
  };

  const onEmailChange = (email: string) => {
    setEmail(email);
  };

  const onOwnerChange = (owner: string) => {
    setOwner(owner);
  };

  const onDetailsChange = (details: string) => {
    setDetails(details);
  };

  const onSenderChange = (sender: string) => {
    setSender(sender);
  };

  const onProspectCategoryChange = (prospectCategory: string) => {
    setProspectCategory(prospectCategory);
  };

  const onCompanyChange = (company: string) => {
    setCompany(company);
  };

  const onAdresseChange = (adresse: string) => {
    setAdresse(adresse);
  };

  const onRoleChange = (role: string) => {
    setRole(role);
  };

  const onWebsiteChange = (website: string) => {
    setWebsite(website);
  };

  const onContactScheduledChange = (newContactScheduled: boolean) => {
    setContactScheduled(newContactScheduled);
  };

  const onContactScheduledDateChange = (newContactScheduledDate: Dayjs | null) => {
    setContactScheduledDate(newContactScheduledDate);
  };

  const submit = async () => {
    if (subscription === null || authContext.authentication === null) return;
    setStatus(ContextStatus.LOADING);
    try {
      await createContact(
        new CreateContactPayload(
          subscription,
          civility,
          firstname,
          lastname,
          phone,
          email,
          owner,
          details,
          sender,
          prospectCategory,
          company,
          adresse,
          role,
          website,
          contactScheduled,
          contactScheduledDate
        ),
        authContext.authentication
      );

      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof CreateContactFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <ContactCreationFormContext.Provider
      value={{
        status,
        subscription,
        subscriptionOwners,
        subscriptionScenarios,
        civility,
        firstname,
        lastname,
        phone,
        email,
        owner,
        details,
        sender,
        prospectCategory,
        company,
        adresse,
        role,
        website,
        contactScheduled,
        contactScheduledDate,
        errorMessage,
        initWithSubscriptionId,
        onCivilityChange,
        onFirstnameChange,
        onLastnameChange,
        onPhoneChange,
        onEmailChange,
        onOwnerChange,
        onDetailsChange,
        onSenderChange,
        onProspectCategoryChange,
        onCompanyChange,
        onAdresseChange,
        onRoleChange,
        onWebsiteChange,
        onContactScheduledChange,
        onContactScheduledDateChange,
        submit,
      }}
    >
      {children}
    </ContactCreationFormContext.Provider>
  );
};

export default ContactCreationFormContext;
